/* @flow */
import * as React from 'react';
import invariant from 'invariant';

import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import CheckboxList from 'components/CheckboxList';
import CurrentUser from 'auth/CurrentUser';
import DatePickerInput from 'components/DatePickerInput';
import Dialog from 'components/Dialog';
import enumerate from 'lib/enumerate';
import Field from 'components/Field';
import Heading from 'components/Heading';
import LinkConstants from 'constants/LinkConstants';
import LoadingIndicator from 'components/LoadingIndicator';
import MailTo from 'components/MailTo';
import OrganizationActions from 'actions/OrganizationActions';
import Radio from 'components/Radio';
import Text from 'components/Text';
import Textarea from 'components/Textarea';
import ToastActions from 'actions/ToastActions';

import idx from 'idx';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';

import './styles/RequestTranscriptModal.scss';

type User = {|
  id: Backbone$Id,
  fullName: string,
  firstName: string,
  canGetMessageHistory: boolean,
  canRequestMessageHistory: boolean,
|};

type GeneratedProps = {
  organization: {
    id: Backbone$Id,
    name: string,
    type: string,
    paidAt: string,
    parentId: Backbone$Id,
    isParentOrgAdmin: boolean,
  },
};

type RequiredProps = {
  organizationId: string | number,

  users: User[],
  onRequestHide?: Function,
  // When true this will force to request of the message history
  // and ignore the user permissions. We use this for admins when they
  // look at the people list outside of the admin console. We still want
  // them to request message history there even if they have the permission to get them.
  forceRequestMessageHistory?: boolean,
  onGetTranscript?: (requested: boolean) => void,
};

type Props = RequiredProps & GeneratedProps;

const STATUS = enumerate(['request_from', 'message_types', 'request_date', 'request_message']);

type State = {
  note: string,
  messageTypes: Array<string>,
  startDate: Date,
  status: number,
  requestFromAdmin: boolean,
};

const shouldShowRequestFromScreen = org => org && org.parentId && !org.isParentOrgAdmin;

function getYearAgo() {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return date;
}

// XXX this logic should come from the backend
// I'm just refactoring for https://app.asana.com/0/inbox/52068106376727/361768969180467/361768969180473
function getStartDate(orgPaidAt: ?string) {
  const yearAgo = getYearAgo();
  const date = new Date(
    // $FlowFixMe - no type coercion for 0.93.0
    Math.max(orgPaidAt ? new Date(orgPaidAt) : yearAgo, yearAgo)
  );

  // hack around https://github.com/remind101/r101-api/pull/10744
  date.setDate(date.getDate() + 1);
  return date;
}

class RequestTranscriptModal extends React.Component<Props, State> {
  state: State = {
    note: '',
    messageTypes: ['inclChats', 'inclAnnouncements'],
    status: shouldShowRequestFromScreen(this.props.organization)
      ? STATUS.request_from
      : STATUS.message_types,
    startDate: this.props.organization ? getStartDate(this.props.organization.paidAt) : new Date(),
    requestFromAdmin: false,
  };

  componentWillMount() {
    const note = this.getNoteText(false);

    this.setState({ note });
  }

  componentWillReceiveProps(newProps: Props) {
    if (!this.props.organization && newProps.organization) {
      const newOrg = newProps.organization;

      const startDate = getStartDate(newOrg.paidAt);

      const status = shouldShowRequestFromScreen(newOrg)
        ? STATUS.request_from
        : STATUS.message_types;

      this.setState({ startDate, status });
    }
  }

  setMessageTypes = (messageTypes: Array<string>) => {
    this.setState({ messageTypes });
  };

  handleNoteChange = (event: any) => {
    this.setState({ note: event.target.value });
  };

  setStartDate = (startDate: any) => {
    this.setState({ startDate });
  };

  setRequestFromTeacher = () => {
    this.setState({ note: this.getNoteText(false), requestFromAdmin: false });
  };

  setRequestFromAdmin = () => {
    this.setState({ note: this.getNoteText(true), requestFromAdmin: true });
  };

  getNoteText = (requestFromAdmin: boolean) => {
    const name = this.props.users.length === 1 ? this.props.users[0].firstName : null;

    return (
      (requestFromAdmin
        ? `Hi, I’d like to request the message history of ${name || 'several people'}.`
        : `Hi${name ? ' ' + name : ''}, I’d like to learn how you’re using Remind.`) +
      ' Feel free to reach out to me if you have any questions.'
    );
  };

  canGetAllMessageHistory = () => {
    return (
      !this.props.forceRequestMessageHistory && this.props.users.every(u => u.canGetMessageHistory)
    );
  };

  getEmailLink = () => {
    const { users } = this.props;
    const email = 'schooladmin@remindhq.com';
    const subject = 'Urgent request for message history';
    const body = [
      'I’d like to request the message history of a person at my school as part of an active investigation.\n',
      `User Ids: ${users.map(u => u.id).join(', ')}\n`,
      `School: ${this.props.organization.name}`,
      'Description of investigation:\n',
      'Please contact me as soon as possible,',
      CurrentUser.getUser().getSignature(),
    ].join('\n');
    return `mailto:${email}?subject=${subject}&body=${encodeURIComponent(body)}`;
  };

  getRequestDestination = () => {
    return this.state.requestFromAdmin ? 'your district administrator' : this.getUsersDescription();
  };

  getUsersDescription = () => {
    const { users } = this.props;

    if (users.length === 1) {
      return users[0].fullName || '1 person';
    }
    return `${this.props.users.length} people`;
  };

  invalid = () => {
    return this.state.messageTypes.length === 0;
  };

  requestTranscript = () => {
    const { onRequestHide, organizationId, users, onGetTranscript } = this.props;
    invariant(onRequestHide, 'onRequestHide required in a modal');
    const onSuccess = () => {
      ToastActions.showSuccessToast(`Message history for ${this.getUsersDescription()} requested`);
      onRequestHide();
    };
    const onError = function(err) {
      ToastActions.showErrorToast(err.message || 'Something went wrong. Please try again.');
    };
    const transcripts = users.map(user => {
      return {
        user_id: user.id,
        request_from_affected_user:
          this.props.forceRequestMessageHistory ||
          !user.canGetMessageHistory ||
          this.startDateOnOrBeforePaid(),
      };
    });
    OrganizationActions.batchRequestTranscript(
      organizationId,
      transcripts,
      this.state.note,
      this.state.messageTypes.includes('inclChats'),
      this.state.messageTypes.includes('inclAnnouncements'),
      this.state.startDate,
      this.state.requestFromAdmin
    ).then(onSuccess, onError);

    if (onGetTranscript) {
      onGetTranscript(this.canGetAllMessageHistory());
    }
  };
  showRequestFrom = () =>
    this.setState({
      status: STATUS.request_from,
    });
  showMessageTypes = () =>
    this.setState({
      status: STATUS.message_types,
    });
  showRequestDate = () =>
    this.setState({
      status: STATUS.request_date,
    });
  showRequestMessage = () =>
    this.setState({
      status: STATUS.request_message,
    });
  renderRequestFromBody = () => {
    return (
      <div>
        <Heading align="left" level="3" levelStyle="5">
          How would you like to receive the message history?
        </Heading>
        <Field>
          <Radio
            name="request_from"
            checked={!this.state.requestFromAdmin}
            onChange={this.setRequestFromTeacher}
            label="Request from teacher"
            labelNote="You may choose to request message history directly from the teacher."
          />
          <Radio
            name="request_from"
            data-test="admin-radio"
            onChange={this.setRequestFromAdmin}
            checked={this.state.requestFromAdmin}
            label="Request from your district admin"
            labelNote="Your district administrator can access message history for you."
          />
        </Field>
        <Buttons align="right">
          <Button data-test="selected-request-from-button" onClick={this.showMessageTypes}>
            Next
          </Button>
        </Buttons>
      </div>
    );
  };

  getRequestDateInfo = () => {
    return this.canGetAllMessageHistory()
      ? 'You can choose to get message history from the start date of your Remind plan. Any date prior to the start of your plan will send a request to the user.'
      : 'District Administrators can give you access up to the start date of your Remind plan, and the user can give you up to a year.';
  };

  // XXX around the logic in backend
  // see https://github.com/remind101/r101-api/pull/10744
  // good to come back to this once that's cleared up.
  startDateOnOrBeforePaid = () => {
    const paidDay = new Date(this.props.organization.paidAt);
    paidDay.setHours(0, 0, 0, 0);
    const startDay = new Date(this.state.startDate);
    startDay.setHours(0, 0, 0, 0);
    return startDay <= paidDay;
  };

  renderMessageTypeBody = () => {
    const canGetAllMessageHistory = this.canGetAllMessageHistory();
    return (
      <div>
        <Heading align="left" level="3" levelStyle="5">
          What would you like to view?
        </Heading>
        <Text align="left" color="thunder" element="p" size="xxsmall">
          {canGetAllMessageHistory
            ? 'You’ll receive an email once you have access to the selected message history.'
            : 'This request will be sent to this person, and you’ll receive an email once you have access to the selected message history. You can also join the person’s class to view announcements.'}
        </Text>
        <Field>
          <CheckboxList
            checked={this.state.messageTypes}
            options={
              new Map([
                ['inclAnnouncements', 'Class announcements'],
                ['inclChats', 'Individual and group messages'],
              ])
            }
            onChange={this.setMessageTypes}
          />
        </Field>
        <Buttons split align="right">
          {shouldShowRequestFromScreen(this.props.organization) && (
            <Button level="secondary" onClick={this.showRequestFrom}>
              Back
            </Button>
          )}
          <Button data-test="selected-message-type-button" onClick={this.showRequestDate}>
            Next
          </Button>
        </Buttons>
      </div>
    );
  };
  renderRequestDateBody = () => {
    const shouldRequest = !this.canGetAllMessageHistory() || this.startDateOnOrBeforePaid();
    const minDate = this.state.requestFromAdmin
      ? getStartDate(this.props.organization.paidAt)
      : getYearAgo();
    return (
      <div>
        <Heading level="3" levelStyle="5" align="left">
          What date would you like message history from?
        </Heading>
        <Text color="thunder" element="p" size="xxsmall" align="left">
          {this.getRequestDateInfo()} Messages will be delivered to you via email and may take up to
          30 minutes. If this request is part of an active investigation,{' '}
          <MailTo to={LinkConstants.SUPPORT_EMAIL}>please contact our Support team</MailTo>.
        </Text>
        <Field className="RequestTranscriptModal-dateRange">
          <DatePickerInput
            className="RequestTranscriptModal-dateRange-picker"
            value={this.state.startDate}
            min={minDate}
            max={new Date()}
            onChange={this.setStartDate}
            placement="bottom"
          />
          <span className="RequestTranscriptModal-dateRange-separator"> – </span>
          <DatePickerInput
            className="RequestTranscriptModal-dateRange-picker"
            placeholder="Today"
            placement="bottom"
            disabled
          />
        </Field>
        {this.canGetAllMessageHistory() &&
          shouldRequest && (
            <Text align="left" color="thunder" element="p" size="xxsmall">
              Messages from before the start of your Remind plan will require approval from{' '}
              {this.getRequestDestination()}
            </Text>
          )}
        <Buttons split>
          <Button level="secondary" onClick={this.showMessageTypes}>
            Back
          </Button>
          <Button
            data-test="selected-date-range-button"
            onClick={shouldRequest ? this.showRequestMessage : this.requestTranscript}>
            {shouldRequest ? 'Next' : 'Access messages'}
          </Button>
        </Buttons>
      </div>
    );
  };
  renderRequestMessageBody = () => {
    return (
      <div>
        <Heading level="3" levelStyle="5" align="left">
          Customize request message
        </Heading>
        <Text align="left" color="thunder" element="p" size="xxsmall">
          An email will be sent to {this.getRequestDestination()} requesting access to the message
          history. You’ll receive the selected message history via email.
        </Text>
        <Field>
          <Textarea
            className="RequestTranscriptModal-requestMessage"
            value={this.state.note}
            onChange={this.handleNoteChange}
            autogrow
          />
        </Field>
        <Buttons split>
          <Button level="secondary" onClick={this.showRequestDate}>
            Back
          </Button>
          <Button data-test="request-transcript-button" onClick={this.requestTranscript}>
            Request Messages
          </Button>
        </Buttons>
      </div>
    );
  };
  renderBody = () => {
    if (!this.props.organization) {
      return <LoadingIndicator />;
    }
    switch (this.state.status) {
      case STATUS.request_from:
        return this.renderRequestFromBody();
      case STATUS.message_types:
        return this.renderMessageTypeBody();
      case STATUS.request_date:
        return this.renderRequestDateBody();
      case STATUS.request_message:
        return this.renderRequestMessageBody();
      default:
        console.error('Unknown step');
        this.showMessageTypes();
        return <div />;
    }
  };
  render() {
    const { onRequestHide } = this.props;
    const canGetAllMessageHistory = this.canGetAllMessageHistory();
    invariant(onRequestHide, 'onRequestHide required in a modal');
    return (
      <Dialog
        eventScreenName="RequestTranscriptModal"
        className="RequestTranscriptModal"
        image={require('assets/images/organizations/RequestTranscriptModal/woman-paper-airplane.svg')}
        imageSize="xxxsmall"
        size="small"
        onRequestHide={onRequestHide}
        title={`${
          canGetAllMessageHistory ? 'Get message history for' : 'Request message history for'
        } ${this.getUsersDescription()}`}>
        <BoxBody divided>{this.renderBody()}</BoxBody>
      </Dialog>
    );
  }
}
const withOrganizationData = graphql(
  gql`
    query RequestTranscriptModal($organizationId: Int) {
      organizations(ids: [$organizationId]) {
        id
        uuid
        name
        type
        paidAt
        parentId
        isParentOrgAdmin
      }
    }
  `,
  {
    options: ({ organizationId }) => ({
      variables: {
        organizationId,
      },
    }),
    props: ({ data }) => ({
      organization: idx(data, _ => _.organizations[0]),
    }),
  }
);
export { RequestTranscriptModal };
export default (withOrganizationData(RequestTranscriptModal): React$ComponentType<RequiredProps>);
